// src/app/(home)/PageClient.tsx
'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import style from './PageClient.module.scss';

// 元件
import BottomNav from '@/components/bottomNav/BottomNav';
import { IconAR, IconArrow, IconFriends, IconLocation } from '@/components/icons';
import Avatar from '@/components/ui/avatar/Avatar';
import ArScanModal from '@/components/ui/modal/ArScanModal';
import BgParallax from '@/components/page/ar-space/BgParallax';
import CoinCanvas from '@/components/page/ar-space/CoinCanvas';
import MapModal from '@/components/ui/modal/MapModal';

// 工具
import { showApiError } from '@/libs/helpers/showApiState';
import { useAppSelector } from '@/store/hook';

// data
import { ArSpaceService } from '@/libs/services/arSpace';
import type { ArSpaceIndexItem } from '@/types/arSpace';
import LazyImage from '@/components/ui/lazyImage/LazyImage';

const SWIPE_THRESHOLD = 50;

// ✅ 防後端 type 髒資料（空白/附註）導致走錯 API
function normalizeSpaceType(t: unknown): 'branch' | 'ar_space' {
  const s = String(t ?? '').trim();
  if (s === 'branch' || s.startsWith('branch')) return 'branch';
  if (s === 'ar_space' || s.startsWith('ar_space')) return 'ar_space';
  // 最後保底（避免整個壞掉）：包含 branch 就當 branch，否則當 ar_space
  return s.includes('branch') ? 'branch' : 'ar_space';
}

const PageClient = () => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const token = useAppSelector((state) => state.auth.token);

  const [arSpaceList, setArSpaceList] = useState<ArSpaceIndexItem[]>([]);

  // ✅ 後端 index_ar_space 不回 article_data 了：CoinCanvas 改用這個 state
  const [currentArticles, setCurrentArticles] = useState<
    NonNullable<ArSpaceIndexItem['article_data']>
  >([]);

  // 文章請求 race guard
  const articlesReqSeqRef = useRef(0);

  // 列表滾動用 ref（控制「選到的項目捲到最上方」）
  const scrollListRef = useRef<HTMLDivElement | null>(null);

  const getIndexArSpace = async () => {
    try {
      const list = await ArSpaceService.getIndexArSpace({ withAuth: false });
      setArSpaceList(list);
    } catch (err) {
      showApiError(err, '取得AR空間失敗');
    }
  };

  useEffect(() => {
    void getIndexArSpace();
  }, []);

  // body 鎖住捲動
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  const [currentIndex, setCurrentIndex] = useState(0);

  // CoinCanvas loading 動畫狀態
  const [coinLoading, setCoinLoading] = useState(false);
  const coinLoadingTimeoutRef = useRef<number | null>(null);

  const startCoinLoading = () => {
    if (typeof window === 'undefined') return;
    setCoinLoading(true);
    if (coinLoadingTimeoutRef.current != null) {
      window.clearTimeout(coinLoadingTimeoutRef.current);
    }
    coinLoadingTimeoutRef.current = window.setTimeout(() => {
      setCoinLoading(false);
    }, 400);
  };

  useEffect(
    () => () => {
      if (typeof window !== 'undefined' && coinLoadingTimeoutRef.current != null) {
        window.clearTimeout(coinLoadingTimeoutRef.current);
      }
    },
    []
  );

  const [scrollOffsetX, setScrollOffsetX] = useState(0);
  const touchStartYRef = useRef<number | null>(null);
  const [listExpanded, setListExpanded] = useState(false);

  const handleToggleList = () => setListExpanded((prev) => !prev);

  const hasSpace = arSpaceList.length > 0;
  const currentSpace = hasSpace ? arSpaceList[currentIndex] : undefined;

  const currentSpaceType = currentSpace ? normalizeSpaceType(currentSpace.type) : undefined;

  const bgImage = currentSpace?.background || currentSpace?.photo || '';

  const ownerHref =
    currentSpace != null && currentSpaceType != null
      ? `${currentSpaceType === 'branch' ? '/store' : '/user'}/${currentSpace.owner_open_id}`
      : '';

  const addArticleHref =
    currentSpace == null || currentSpaceType == null
      ? undefined
      : currentSpaceType === 'branch'
        ? `/me/article/new?branch_id=${currentSpace.type_id}`
        : `/me/article/new?ar_space_id=${currentSpace.type_id}`;

  // ✅ 當 currentSpace 改變，就去 detail API 把 article_data 撈回來
  useEffect(() => {
    if (!currentSpace || !currentSpaceType) {
      setCurrentArticles([]);
      return;
    }

    const reqId = ++articlesReqSeqRef.current;

    setCurrentArticles([]);
    startCoinLoading();

    (async () => {
      try {
        const articles = await ArSpaceService.getIndexSpaceArticles(
          { type: currentSpaceType, type_id: currentSpace.type_id },
          { withAuth: false }
        );

        if (articlesReqSeqRef.current !== reqId) return;
        setCurrentArticles(articles ?? []);
      } catch (err) {
        if (articlesReqSeqRef.current !== reqId) return;
        showApiError(err, '取得入住文章失敗');
        setCurrentArticles([]);
      }
    })();
  }, [currentSpace, currentSpaceType]);

  const goToSpace = (nextIndex: number) => {
    if (!hasSpace) return;
    const total = arSpaceList.length;
    const normalized = ((nextIndex % total) + total) % total;

    if (normalized !== currentIndex) startCoinLoading();
    setCurrentIndex(normalized);

    if (scrollListRef.current) {
      const container = scrollListRef.current;
      const target = container.querySelector<HTMLElement>(`[data-index="${normalized}"]`);
      if (target) {
        container.scrollTo({
          top: target.offsetTop - 50,
          behavior: 'smooth',
        });
      }
    }
  };

  const handleNextSpace = () => goToSpace(currentIndex + 1);
  const handlePrevSpace = () => goToSpace(currentIndex - 1);

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    touchStartYRef.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    if (touchStartYRef.current == null) return;

    const endY = e.changedTouches[0].clientY;
    const deltaY = endY - touchStartYRef.current;

    if (deltaY < -SWIPE_THRESHOLD) handleNextSpace();
    else if (deltaY > SWIPE_THRESHOLD) handlePrevSpace();

    touchStartYRef.current = null;
  };

  /** Modal 狀態 */
  const [scanOpen, setScanOpen] = useState(false);
  const [scanPic, setScanPic] = useState<string | undefined>(undefined);
  const [mapOpen, setMapOpen] = useState(false);
  const [mapTitle, setMapTitle] = useState('');
  const [mapQuery, setMapQuery] = useState('');

  const openScan = (item: ArSpaceIndexItem) => {
    setScanPic(item.photo);
    setScanOpen(true);
  };

  const handleClickArScan = useCallback(() => {
    if (!currentSpace) return;
    openScan(currentSpace);
  }, [currentSpace]);

  const openMap = (item: ArSpaceIndexItem) => {
    const displayName = item.branch_name;
    const query = item.branch_name?.trim() || displayName || item.title;
    setMapTitle(displayName);
    setMapQuery(query);
    setMapOpen(true);
  };

  const handleClickSpace = (space: ArSpaceIndexItem, index: number) => {
    goToSpace(index);

    const spaceType = normalizeSpaceType(space.type);

    const params = new URLSearchParams(searchParams.toString());
    params.delete('branch_id');
    params.delete('ar_space_id');

    const key = spaceType === 'branch' ? 'branch_id' : 'ar_space_id';
    params.set(key, String(space.type_id));

    const queryString = params.toString();
    router.push(queryString ? `${pathname}?${queryString}` : pathname, { scroll: false });
  };

  useEffect(() => {
    if (!arSpaceList.length) return;

    const branchId = searchParams.get('branch_id');
    const arSpaceId = searchParams.get('ar_space_id');

    let nextIndex = 0;

    if (branchId) {
      const idx = arSpaceList.findIndex(
        (space) => normalizeSpaceType(space.type) === 'branch' && String(space.type_id) === branchId
      );
      if (idx !== -1) nextIndex = idx;
    } else if (arSpaceId) {
      const idx = arSpaceList.findIndex(
        (space) =>
          normalizeSpaceType(space.type) === 'ar_space' && String(space.type_id) === arSpaceId
      );
      if (idx !== -1) nextIndex = idx;
    }

    goToSpace(nextIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arSpaceList, searchParams]);

  const coinCanvasKey =
    currentSpace && currentSpaceType
      ? `${currentSpaceType}_${String(currentSpace.type_id)}`
      : 'none';

  return (
    <>
      <BgParallax
        bgImage={bgImage}
        scrollOffsetX={scrollOffsetX}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div className="container-lg">
          <div className="position-relative z-50 d-flex align-items-center justify-content-between pt-2">
            <div className="d-flex flex-shrink-1 flex-grow-1 min-w-0">
              {currentSpace && (
                <div className="flex-shrink-0">
                  <Link href={ownerHref}>
                    <Avatar src={currentSpace.owner_photo} size={40} />
                  </Link>
                </div>
              )}
              <div className="d-flex flex-column flex-shrink-1 min-w-0">
                {currentSpace && (
                  <Link href={ownerHref}>
                    <span className="text-white fs-sm-1 ms-2 text-shadow-1 text-truncate-1">
                      {currentSpace.owner_name}
                    </span>
                  </Link>
                )}

                {currentSpace?.branch_name?.trim() ? (
                  <button
                    type="button"
                    onClick={() => currentSpace && openMap(currentSpace)}
                    aria-label={`開啟 ${currentSpace?.branch_name} 的地圖`}
                    title="查看地圖"
                    className="d-flex align-items-center text-grey-50 fs-xs ms-2 text-center text-shadow-1"
                  >
                    <span className="flex-shrink-0">
                      <IconLocation width={12} />
                    </span>
                    <span className="flex-shrink-1 text-truncate-1 flex-grow-1 min-w-0">
                      {currentSpace?.branch_name}
                    </span>
                  </button>
                ) : (
                  <div className="d-flex align-items-center text-grey-50 fs-xs ms-2 text-center text-shadow-1">
                    <span className="flex-shrink-0">
                      <IconLocation width={12} />
                    </span>
                    <span className="flex-shrink-1 text-truncate-1 flex-grow-1 min-w-0">
                      {currentSpace?.branch_name}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="text-dark fs-xs bg-white rounded-pill py-1 px-2 flex-shrink-0">
              <IconFriends width={16} />
              <span className="ms-2">{currentSpace?.article_num}人住在這裡</span>&nbsp;
            </div>
          </div>
        </div>

        <CoinCanvas
          key={coinCanvasKey}
          articles={currentArticles}
          onScrollParallaxChange={setScrollOffsetX}
          onClickArScan={handleClickArScan}
          addArticleHref={addArticleHref}
          loading={coinLoading}
        />
      </BgParallax>

      <div className={`text-white ${style.listBlock} ${listExpanded ? style.on : ''}`}>
        <div className="container-lg">
          <div className="d-flex align-items-center justify-content-between">
            <h6 className="py-3 flex-shrank-1 flex-grow-1">AR探索社交亮點</h6>

            <div className="btn btn-round btn-sm text-grey-300">
              <Link href={'/ar-space'}>看更多</Link>
            </div>

            <button
              type="button"
              className={`text-white ${style.btnMoreList} ${listExpanded ? style.active : ''}`}
              onClick={handleToggleList}
              aria-expanded={listExpanded}
            >
              <IconArrow width={24} />
            </button>
          </div>

          <div
            className={`row p-2 pt-0 ${style.scrollList} ${listExpanded ? style.scrollListExpanded : ''}`}
            ref={scrollListRef}
          >
            {arSpaceList.map((space, index) => {
              const spaceType = normalizeSpaceType(space.type);

              return (
                <article
                  key={`${spaceType}_${String(space.type_id)}`}
                  data-index={index}
                  className="col-12 col-md-6 col-lg-6 px-0 p-md-1"
                  onClick={() => handleClickSpace(space, index)}
                >
                  <div
                    className={`d-flex align-items-center justify-content-between mb-1 p-2 rounded-1 ${
                      index === currentIndex ? 'border' : ''
                    } ${style.itemList}`}
                  >
                    <div className="d-flex align-items-center">
                      <div
                        className="flex-shrink-0"
                        style={{
                          position: 'relative',
                          width: '100px',
                          height: '60px',
                          borderRadius: '8px',
                        }}
                      >
                        <div
                          className="position-absolute"
                          style={{ width: '100px', height: '60px', borderRadius: '8px' }}
                        >
                          <LazyImage
                            src={space.background ?? space.photo}
                            alt={space.title}
                            aspect="5/3"
                            imgStyle={{ objectFit: 'cover' }}
                            radius={8}
                          />
                        </div>

                        <div className="mt-1 ms-1">
                          <Avatar src={space.owner_photo} size={28} />
                        </div>
                      </div>

                      <div className="ms-3 text-start">
                        <h6 className="fs-sm-1 mb-1 me-1">{space.title}</h6>
                        <p className="fs-xs text-grey-200 mb-0">
                          <IconLocation width={14} />
                          {space.branch_name}
                        </p>
                      </div>
                    </div>

                    <div className="d-flex ms-1">
                      {token ? (
                        <button
                          type="button"
                          className="btn btn-light btn-sm rounded-pill text-nowrap me-1"
                          onClick={(e) => {
                            // 1. 阻止事件冒泡與預設行為
                            e.preventDefault();
                            e.stopPropagation();

                            // 2. 決定基礎路徑
                            const baseHref =
                              spaceType === 'branch'
                                ? `/me/article/new?branch_id=${space.type_id}`
                                : `/me/article/new?ar_space_id=${space.type_id}`;

                            // 3. 使用 window.location.href 強制刷新並跳轉
                            window.location.href = `${baseHref}&t=${Date.now()}`;
                          }}
                        >
                          寫文入住
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-light btn-sm rounded-pill text-nowrap me-1"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const redirectUrl = window.location.pathname + window.location.search;

                            router.push(`/auth/login?redirect=${encodeURIComponent(redirectUrl)}`);
                          }}
                        >
                          寫文入住
                        </button>
                      )}

                      {/* <Link
                        className="btn btn-light btn-sm rounded-pill text-nowrap me-1"
                        href={
                          spaceType === 'branch'
                            ? `/me/article/new?branch_id=${space.type_id}`
                            : `/me/article/new?ar_space_id=${space.type_id}`
                        }
                        onClick={(e) => e.stopPropagation()}
                      >
                        寫文入住
                      </Link> */}

                      <button
                        type="button"
                        className="bg-red text-white rounded-pill text-red text-nowrap px-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          openScan(space);
                        }}
                      >
                        <IconAR width={25} />
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}

            <div className="p-3 px-md-1 mb-md-0">
              <Link href="/ar-space" className="btn btn-light fs-sm-1 w-100">
                看更多AR空間
              </Link>
            </div>
          </div>
        </div>
      </div>

      <BottomNav />

      <ArScanModal
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        scanPic={scanPic}
        onScan={() => {
          console.log('準備掃描中');
          setScanOpen(false);
        }}
      />

      <MapModal
        open={mapOpen}
        title={mapTitle}
        query={mapQuery}
        onClose={() => setMapOpen(false)}
      />
    </>
  );
};

export default PageClient;
